// Wayfarers: other trainers on your hunt area (server/wayfarers.ts sends who and where).
// Each is drawn on the hunt scene (client/field.ts) walking behind their lead, which
// fights its own wild: theirs, never yours, so it is drawn faded and can't be clicked.
// Their World and Region chat lines pop up over their heads, and an Aura they catch
// floats up over them for everyone to see.
import { CONFIG } from '../shared/config.ts';
import { GROUND } from '../shared/grounds.ts';
import type { Broadcast, ClientMsg, CompanionRef } from '../shared/protocol.ts';
import { S } from './state.ts';
import { chatShown } from './chatfilter.ts';
import { chatHidden } from './social.ts';
import { EMOTE, EMOTE_GAP_MS, EMOTE_MS, ownsEmote } from '../shared/kinhall.ts';
import { readMoves } from '../shared/wayfarer-moves.ts';

interface Rec { n: string; o: string; lv: number; tag?: string; lead?: CompanionRef; x: number; y: number; w?: { s: string; x: number; y: number } }

export interface Wayfarer {
  rec: Rec;
  /** The lead as drawn: walks toward rec.x/y. */
  x: number; y: number;
  /** The trainer as drawn: keeps a step behind the lead. */
  tx: number; ty: number; dir: 'down' | 'up' | 'left' | 'right'; moved: number;
  /** Their wild as drawn. */
  wx: number; wy: number;
  bubble?: { text: string; until: number };
  /** An Aura they just caught, floating over them. */
  show?: { species: string; skin?: string; t0: number };
  /** An emote they just played. */
  emote?: { id: string; at: number };
}

const others = new Map<string, Wayfarer>();
let ground = '';
/** How long a caught Aura floats over its catcher. */
export const SHOW_MS = 6000;
/** Your own Aura catch, shown over you the same way. */
let mine: { species: string; skin?: string; t0: number } | null = null;
export const myShowcase = () => mine;
/** Your own emote on the hunt, drawn over you. */
let myEm: { id: string; at: number } | null = null;
export const myEmote = () => (myEm && performance.now() - myEm.at < EMOTE_MS ? myEm : null);

/** Play an emote you own on the hunt: you see it at once; everyone on your hunt sees it too. */
export function playHuntEmote(id: string) {
  if (!S.trainer || !ownsEmote(S.trainer, id)) return;
  const now = performance.now();
  if (myEm && now - myEm.at < EMOTE_GAP_MS) return;
  myEm = { id, at: now };
  send({ t: 'wf', emote: id });
}
/** Someone on your hunt played an emote (your own echo is already showing). */
export function onWayfarerEmote(m: { n: string; id: string }) {
  if (!EMOTE[m.id] || m.n === S.trainer?.name) return;
  const w = others.get(m.n);
  if (w) w.emote = { id: m.id, at: performance.now() };
}

// ---------------- the setting ----------------
let shown = (() => { try { return localStorage.getItem('mi-wayfarers') !== '0'; } catch { return true; } })();
export const showWayfarers = () => shown;
export function setShowWayfarers(on: boolean) {
  shown = on;
  try { localStorage.setItem('mi-wayfarers', on ? '1' : '0'); } catch { /* storage off */ }
  if (!on) others.clear();
  syncWatch();
}

// ---------------- watching ----------------
let send: (m: ClientMsg) => void = () => {};
let isVisible: () => boolean = () => false;
let told: boolean | null = null;
/** Start telling the server whether the hunt scene is on screen (it sends others only then). */
export function mountWayfarers(s: (m: ClientMsg) => void, visible: () => boolean) {
  send = s; isVisible = visible;
  setInterval(syncWatch, 1000);
  document.addEventListener('visibilitychange', syncWatch);
}
function syncWatch() {
  const want = shown && document.visibilityState === 'visible' && isVisible();
  if (want === told) return;
  told = want;
  if (!want) others.clear();
  send({ t: 'wf', on: want });
}
/** After a reconnect the server has forgotten; say it again. */
export function wayfarersReconnected() { told = null; others.clear(); syncWatch(); }

// ---------------- messages ----------------
/** all: everyone on your shard; up: those new or whose look changed, whole; mv: those who only walked (shared/wayfarer-moves.ts). */
export function onWayfarers(m: { ground?: string; all?: Rec[]; up?: Rec[]; mv?: (string | number)[]; gone?: string[] }) {
  if (!shown) return;
  if (m.all) { others.clear(); ground = m.ground ?? ''; }
  for (const r of [...(m.all ?? []), ...(m.up ?? []), ...readMoves(m.mv ?? [], (n) => others.get(n)?.rec)]) {
    if (r.n === S.trainer?.name) continue;
    const w = others.get(r.n);
    if (!w) {
      others.set(r.n, { rec: r, x: r.x, y: r.y, tx: r.x - 0.8, ty: r.y + 0.3, dir: 'down', moved: 0, wx: r.w?.x ?? r.x, wy: r.w?.y ?? r.y });
      continue;
    }
    // A long jump (a new spot on the map) is a cut, not a walk.
    if (Math.hypot(r.x - w.x, r.y - w.y) > 8) { w.x = r.x; w.y = r.y; w.tx = r.x - 0.8; w.ty = r.y + 0.3; }
    if (r.w && (!w.rec.w || w.rec.w.s !== r.w.s)) { w.wx = r.w.x; w.wy = r.w.y; }
    w.rec = r;
  }
  for (const n of m.gone ?? []) others.delete(n);
}

/** Big-catch and chat lines: bubbles over speakers here, and an Aura caught here floats up over its catcher. */
let lastBc = 0;
function readFeed(now: number) {
  const b = S.broadcasts;
  if (!b.length || b[b.length - 1].ts <= lastBc) return;
  for (let i = b.length - 1; i >= 0 && b[i].ts > lastBc; i--) {
    const line: Broadcast = b[i];
    if (!line.who) continue;
    if (line.kind === 'aura' && line.species) {
      const show = { species: line.species, skin: line.creature?.skin, t0: now };
      if (line.who === S.trainer?.name) mine = show;
      else { const w = others.get(line.who); if (w) w.show = show; }
      continue;
    }
    if (line.kind !== 'chat' || line.creature || line.listing) continue;
    const ch = line.ch ?? 'world';
    if (ch !== 'world' && ch !== 'region') continue;
    const w = others.get(line.who);
    if (w) w.bubble = { text: bubbleText(line.text), until: now + 6000 };
  }
  lastBc = b[b.length - 1].ts;
}

/** Those to draw this frame, moved on by dtMs: only on the hunt you are on, minus anyone you blocked. */
export function stepWayfarers(dtMs: number, now: number): Wayfarer[] {
  if (!lastBc && S.broadcasts.length) lastBc = S.broadcasts[S.broadcasts.length - 1].ts;
  readFeed(now);
  if (mine && now - mine.t0 > SHOW_MS) mine = null;
  if (!shown || !S.field || S.field.ground !== ground) return [];
  const speed = CONFIG.walkTilesPerSec * 1.15 * (dtMs / 1000);
  const out: Wayfarer[] = [];
  for (const w of others.values()) {
    if (chatHidden(w.rec.n) || !GROUND[ground]) continue;
    if (w.show && now - w.show.t0 > SHOW_MS) w.show = undefined;
    if (w.bubble && w.bubble.until < now) w.bubble = undefined;
    if (w.emote && now - w.emote.at > EMOTE_MS) w.emote = undefined;
    // The lead walks to its last known spot.
    const dx = w.rec.x - w.x, dy = w.rec.y - w.y, d = Math.hypot(dx, dy);
    if (d > 0.01) { const k = Math.min(1, speed / d); w.x += dx * k; w.y += dy * k; }
    if (w.rec.w) { w.wx += (w.rec.w.x - w.wx) * Math.min(1, dtMs / 400); w.wy += (w.rec.w.y - w.wy) * Math.min(1, dtMs / 400); }
    // The trainer keeps about a tile behind.
    const tdx = w.x - w.tx, tdy = w.y - w.ty, td = Math.hypot(tdx, tdy);
    if (td > 1) {
      const k = (td - 1) / td;
      w.tx += tdx * k; w.ty += tdy * k;
      w.dir = Math.abs(tdx) > Math.abs(tdy) ? (tdx > 0 ? 'right' : 'left') : tdy > 0 ? 'down' : 'up';
      w.moved = now;
    } else if (now - w.moved > 300) {
      w.dir = Math.abs(tdx) > Math.abs(tdy) ? (tdx > 0 ? 'right' : 'left') : tdy > 0 ? 'down' : 'up';
    }
    out.push(w);
  }
  return out;
}

/** A chat line as a bubble: the viewer's Chat filter applied, cut to 70 characters. */
function bubbleText(raw: string): string {
  const t = chatShown(raw);
  return t.length > 70 ? `${t.slice(0, 68)}…` : t;
}
